import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { catchError, defaultIfEmpty, EMPTY, exhaustMap, expand, filter, finalize, forkJoin, map, of, reduce, Subject, switchMap, take, tap, throwError, timer } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { CheckboxComponent } from '@components/checkbox/checkbox.component';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { MsfInput } from '@components/ui/forms/input/input';
import { MsfSelect } from '@components/select/select';
import { OrchestrationProcessSelectComponent } from '@app/pages/admin/components/orchestration-process-select/orchestration-process-select';
import { Loading } from '@app/components/ui/loading/loading';
import { AssignmentProcessService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { ProcessSubscriptionService as ProcessSubscriptionApiService } from '@6grain/masofa-monolith-api-client/common/api/processSubscription.service';
import { MonitoredOperationListItemResponse } from '@6grain/masofa-monolith-api-client/common/model/monitoredOperationListItemResponse';
import { ProcessSubscriptionResponse } from '@6grain/masofa-monolith-api-client/common/model/processSubscriptionResponse';
import { SaveProcessSubscriptionRequest } from '@6grain/masofa-monolith-api-client/common/model/saveProcessSubscriptionRequest';
import { QuartzScheduleFieldsComponent } from '@app/pages/admin/components/quartz-schedule-fields/quartz-schedule-fields';
import { AssignmentStartFormComponent } from '@app/pages/admin/orchestration-process-page/components/assignment-start-form/assignment-start-form';
import {
  assignmentStartApiError,
  assignmentStartFormFromJson,
  buildAssignmentStartParameters,
  defaultAssignmentStartForm,
} from '@app/pages/admin/assignment-start.util';
import {
  catalogParametersForSubscription,
  clearContourRestartOwed,
  DOWNLOAD_CONTOUR_MAX_PAGES,
  DOWNLOAD_CONTOUR_PAGE_SIZE,
  DOWNLOAD_CONTOUR_POLL_ATTEMPTS,
  DOWNLOAD_CONTOUR_POLL_INTERVAL_MS,
  downloadContourShouldRestart,
  geometrySignature,
  geometrySourceType,
  inflightAssignmentIds,
  isContourRestartOwed,
  isNewContourAssignment,
  isSatelliteContourProcess,
  markContourRestartOwed,
  preserveSatelliteLookback,
  readStartedAssignmentId,
} from '@app/pages/admin/download-contour.util';
import { looksLikeSafeSatelliteSnapshot } from '@app/pages/admin/process-subscription-form.util';
import {
  SubscriptionFormMode,
  SubscriptionProcessOption,
  emptySubscriptionForm,
  INTERRUPTION_RESUME_SAME,
  isBlankParametersJson,
  subscriptionCopyForm,
  subscriptionFormFromItem,
  subscriptionParametersTemplate,
  subscriptionSafePreviewJson,
} from '@app/pages/admin/process-subscription-form.util';

@Component({
  selector: 'app-subscription-form-dialog',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    DialogModule,
    CheckboxComponent,
    PrimaryButton,
    SecondaryButton,
    MsfInput,
    MsfSelect,
    OrchestrationProcessSelectComponent,
    QuartzScheduleFieldsComponent,
    AssignmentStartFormComponent,
    Loading,
  ],
  templateUrl: './subscription-form-dialog.html',
  styleUrl: './subscription-form-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionFormDialogComponent {
  private readonly service = inject(ProcessSubscriptionApiService);
  private readonly assignments = inject(AssignmentProcessService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly startForm = viewChild(AssignmentStartFormComponent);
  private readonly contourLoad$ = new Subject<string>();

  readonly saved = output<ProcessSubscriptionResponse>();

  readonly visible = signal(false);
  readonly saving = signal(false);
  readonly scheduleValid = signal(true);
  readonly formError = signal('');
  readonly mode = signal<SubscriptionFormMode>('create');
  readonly processOptions = signal<SubscriptionProcessOption[]>([]);
  readonly name = signal('');
  readonly processDefinitionKey = signal('');
  readonly cronExpression = signal('0 0 6 * * ?');
  readonly timeZoneId = signal('UTC');
  readonly parameterSeedJson = signal('');
  readonly isActive = signal(true);
  readonly interruptionPolicy = signal('WaitForNext');
  readonly resumeAttemptLimit = signal(1);
  readonly pauseAfterConsecutiveFailures = signal(0);
  readonly interruptionOptions = [
    { id: 'WaitForNext', label: 'Wait for the next run' },
    { id: INTERRUPTION_RESUME_SAME, label: 'Continue the same assignment' },
  ];
  readonly showResumeLimit = computed(() => this.interruptionPolicy() === INTERRUPTION_RESUME_SAME);
  readonly safePreviewJson = signal('');
  readonly contourLoading = signal(false);
  readonly contourLoaded = signal(false);

  readonly headerKey = computed(() => {
    if (this.mode() === 'edit') {
      return 'Edit subscription';
    }
    return this.mode() === 'copy' ? 'Create copy' : 'Create subscription';
  });
  readonly processLocked = computed(() => this.mode() !== 'create');
  readonly showSafePreview = computed(() => this.mode() !== 'create' && !!this.safePreviewJson());
  readonly canSave = computed(() =>
    !!(this.name() ?? '').trim()
    && !!(this.processDefinitionKey() ?? '')
    && !!(this.cronExpression() ?? '').trim()
    && !!(this.timeZoneId() ?? '').trim()
    && this.scheduleValid()
    && !this.contourLoading()
    && !this.saving()
    && this.failurePolicyValid(),
  );
  readonly failurePolicyValid = computed(() => {
    const pauseAfter = this.pauseAfterConsecutiveFailures();
    const pauseOk = Number.isInteger(pauseAfter) && pauseAfter >= 0 && pauseAfter <= 30;
    if (!this.showResumeLimit()) {
      return pauseOk;
    }
    const limit = this.resumeAttemptLimit();
    return pauseOk && Number.isInteger(limit) && limit >= 1 && limit <= 10;
  });
  readonly showContourHint = computed(() =>
    this.mode() === 'edit'
    && isSatelliteContourProcess(this.processDefinitionKey())
    && this.contourLoaded(),
  );
  readonly showRegionsHint = computed(() => this.showContourHint() && this.loadedGeometrySource() === 'Regions');
  readonly contourStatusKey = signal('');
  readonly loadedGeometrySource = signal('');

  private editingId: string | null = null;
  private baselineSignature: string | null = null;
  private loadedParameters: Record<string, unknown> | null = null;

  constructor() {
    this.contourLoad$.pipe(
      switchMap(subscriptionId => {
        if (!subscriptionId) {
          return of(null);
        }
        return this.service.processSubscriptionsExportGet().pipe(
          map(document => ({
            subscriptionId,
            parameters: catalogParametersForSubscription(document, subscriptionId),
          })),
          catchError(() => of({ subscriptionId, parameters: null })),
        );
      }),
      takeUntilDestroyed(),
    ).subscribe(loaded => {
      if (!loaded) {
        return;
      }
      this.applyLoadedContour(loaded.subscriptionId, loaded.parameters);
    });
  }

  openCreate(processes: SubscriptionProcessOption[], processKey?: string): void {
    const selectedKey = processKey && processes.some(item => item.id === processKey)
      ? processKey
      : (processes[0]?.id ?? '');
    this.applyForm('create', null, emptySubscriptionForm(), '');
    this.processOptions.set(processes);
    this.processDefinitionKey.set(selectedKey);
    this.parameterSeedJson.set(subscriptionParametersTemplate(selectedKey));
    this.clearContourLoad();
    this.visible.set(true);
  }

  openEdit(processes: SubscriptionProcessOption[], item: ProcessSubscriptionResponse): void {
    this.applyForm('edit', item.id ?? null, subscriptionFormFromItem(item), item.safeInputJson);
    this.processOptions.set(processes);
    this.beginContourLoad(item);
    this.visible.set(true);
  }

  openCopy(processes: SubscriptionProcessOption[], item: ProcessSubscriptionResponse): void {
    const copyName = this.translate.instant('Copy of', {
      name: item.name?.trim() || '—',
    });
    this.applyForm('copy', null, subscriptionCopyForm(item, copyName), item.safeInputJson);
    this.processOptions.set(processes);
    this.clearContourLoad();
    this.visible.set(true);
  }

  close(): void {
    if (this.saving()) {
      return;
    }
    this.visible.set(false);
    this.formError.set('');
    this.contourLoad$.next('');
  }

  setName(value: string): void {
    this.name.set(value);
  }

  setProcessDefinitionKey(value: string): void {
    this.processDefinitionKey.set(value);
    if (this.mode() === 'create') {
      this.parameterSeedJson.set(subscriptionParametersTemplate(value));
    }
  }

  setCronExpression(value: string): void {
    this.cronExpression.set(value);
  }

  setTimeZoneId(value: string): void {
    this.timeZoneId.set(value);
  }

  setActive(value: boolean): void {
    this.isActive.set(value);
  }

  setInterruptionPolicy(value: string): void {
    this.interruptionPolicy.set(value);
  }

  setResumeAttemptLimit(value: string | number): void {
    this.resumeAttemptLimit.set(Number(value));
  }

  setPauseAfterConsecutiveFailures(value: string | number): void {
    this.pauseAfterConsecutiveFailures.set(Number(value));
  }

  save(): void {
    if (!this.canSave()) {
      return;
    }
    this.formError.set('');
    const parameters = this.resolveParameters();
    if (parameters === null) {
      return;
    }
    const subscriptionId = this.editingId;
    const geometryChanged = downloadContourShouldRestart({
      mode: this.mode(),
      processKey: this.processDefinitionKey(),
      contourLoaded: this.contourLoaded(),
      baselineSignature: this.baselineSignature,
      restartOwed: !!subscriptionId && isContourRestartOwed(subscriptionId),
      parameters,
    });
    const request: SaveProcessSubscriptionRequest = {
      name: this.name().trim(),
      processDefinitionKey: this.processDefinitionKey(),
      cronExpression: this.cronExpression().trim(),
      timeZoneId: this.timeZoneId().trim(),
      isActive: this.isActive(),
      interruptionPolicy: this.interruptionPolicy(),
      resumeAttemptLimit: this.resumeAttemptLimit(),
      pauseAfterConsecutiveFailures: this.pauseAfterConsecutiveFailures(),
    };
    if (parameters) {
      request.parameters = parameters;
    }
    this.saving.set(true);
    let saved = false;
    const operation = this.editingId
      ? this.service.processSubscriptionsSubscriptionIdPut(this.editingId, request)
      : this.service.processSubscriptionsPost(request);
    operation
      .pipe(
        tap(() => {
          saved = true;
          if (geometryChanged && subscriptionId) {
            markContourRestartOwed(subscriptionId);
            this.contourStatusKey.set('SATELLITE_CONTOUR_RESTARTING');
          }
        }),
        switchMap(response => {
          if (!geometryChanged || !subscriptionId) {
            return of(response);
          }
          return this.restartDownloadContour(subscriptionId).pipe(map(() => response));
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.saving.set(false);
          this.contourStatusKey.set('');
        }),
      )
      .subscribe({
        next: response => {
          if (subscriptionId && geometryChanged) {
            clearContourRestartOwed(subscriptionId);
          }
          this.visible.set(false);
          this.saved.emit((response ?? {}) as ProcessSubscriptionResponse);
        },
        error: error => {
          if (saved && subscriptionId && geometryChanged) {
            markContourRestartOwed(subscriptionId);
          }
          this.formError.set(saved
            ? 'SATELLITE_DOWNLOAD_CONTOUR_RESTART_FAILED'
            : assignmentStartApiError(error, 'Failed to save process subscription'));
        },
      });
  }

  private resolveParameters(): Record<string, unknown> | undefined | null {
    if (
      this.mode() === 'edit'
      && isSatelliteContourProcess(this.processDefinitionKey())
      && !this.contourLoaded()
    ) {
      return undefined;
    }
    const built = this.buildStartParameters();
    const omitUnrestoredDefaults = this.mode() === 'edit'
      && isBlankParametersJson(this.parameterSeedJson());
    if (omitUnrestoredDefaults && !built?.ok) {
      return undefined;
    }
    if (!built) {
      this.formError.set('Parameters are required.');
      return null;
    }
    if (!built.ok) {
      this.formError.set(built.error);
      return null;
    }
    const parameters = this.withSubscriptionName(built.parameters);
    if (omitUnrestoredDefaults) {
      const defaults = buildAssignmentStartParameters(
        this.processDefinitionKey(),
        defaultAssignmentStartForm(this.processDefinitionKey()),
      );
      if (defaults.ok && JSON.stringify(parameters) === JSON.stringify(this.withSubscriptionName(defaults.parameters))) {
        return undefined;
      }
    }
    if (looksLikeSafeSatelliteSnapshot(parameters)) {
      this.formError.set('Sanitized preview cannot be saved as process parameters');
      return null;
    }
    return preserveSatelliteLookback(this.processDefinitionKey(), parameters, this.loadedParameters);
  }

  /** Catalogue parameters repeat the subscription name. Keep one value, the dialog field. */
  private withSubscriptionName(parameters: Record<string, unknown>): Record<string, unknown> {
    if (this.startForm()?.kind() !== 'catalogue') {
      return parameters;
    }
    if (!('name' in parameters) && !('Name' in parameters)) {
      return parameters;
    }
    const copy = { ...parameters };
    delete copy['Name'];
    copy['name'] = this.name().trim();
    return copy;
  }

  private buildStartParameters() {
    return this.startForm()?.build()
      ?? buildAssignmentStartParameters(
        this.processDefinitionKey(),
        assignmentStartFormFromJson(this.processDefinitionKey(), this.parameterSeedJson()),
      );
  }

  private applyForm(
    mode: SubscriptionFormMode,
    editingId: string | null,
    form: ReturnType<typeof emptySubscriptionForm>,
    safeInputJson?: string | null,
  ): void {
    this.mode.set(mode);
    this.editingId = editingId;
    this.formError.set('');
    this.name.set(form.name);
    this.processDefinitionKey.set(form.processDefinitionKey);
    this.cronExpression.set(form.cronExpression);
    this.timeZoneId.set(form.timeZoneId);
    this.parameterSeedJson.set(form.parametersJson);
    this.isActive.set(form.isActive);
    this.interruptionPolicy.set(form.interruptionPolicy);
    this.resumeAttemptLimit.set(form.resumeAttemptLimit);
    this.pauseAfterConsecutiveFailures.set(form.pauseAfterConsecutiveFailures);
    this.safePreviewJson.set(subscriptionSafePreviewJson(safeInputJson));
  }

  private beginContourLoad(item: ProcessSubscriptionResponse): void {
    const subscriptionId = item.id ?? '';
    if (isSatelliteContourProcess(item.processDefinitionKey) && subscriptionId) {
      this.contourLoaded.set(false);
      this.contourLoading.set(true);
      this.baselineSignature = null;
      this.contourLoad$.next(subscriptionId);
      return;
    }
    this.clearContourLoad();
  }

  private clearContourLoad(): void {
    this.contourLoading.set(false);
    this.contourLoaded.set(false);
    this.baselineSignature = null;
    this.loadedParameters = null;
    this.loadedGeometrySource.set('');
    this.contourLoad$.next('');
  }

  private applyLoadedContour(
    subscriptionId: string,
    parameters: Record<string, unknown> | null,
  ): void {
    if (this.mode() !== 'edit' || subscriptionId !== this.editingId) {
      return;
    }
    this.contourLoading.set(false);
    if (!parameters) {
      this.contourLoaded.set(false);
      this.baselineSignature = null;
      this.loadedParameters = null;
      this.loadedGeometrySource.set('');
      this.formError.set('SATELLITE_DOWNLOAD_CONTOUR_LOAD_FAILED');
      return;
    }
    this.loadedParameters = parameters;
    this.loadedGeometrySource.set(geometrySourceType(parameters));
    this.baselineSignature = geometrySignature(parameters);
    this.parameterSeedJson.set(JSON.stringify(parameters, null, 2));
    this.contourLoaded.set(true);
  }

  private restartDownloadContour(subscriptionId: string) {
    return this.allAssignments(subscriptionId).pipe(
      switchMap(items => {
        const previousInflightIds = inflightAssignmentIds(items, 'block');
        const cancelIds = inflightAssignmentIds(items, 'cancel');
        const cancels = cancelIds.length
          ? forkJoin(cancelIds.map(id => this.assignments.assignmentsAssignmentIdCancelPost(id)))
          : of([]);
        return cancels.pipe(
          switchMap(() => this.waitUntilDownloadSlotFree(subscriptionId)),
          switchMap(() => this.service.processSubscriptionsSubscriptionIdRunPost(subscriptionId)),
          switchMap(started => {
            const startedId = readStartedAssignmentId(started);
            if (!isNewContourAssignment(startedId, previousInflightIds)) {
              return throwError(() => new Error('download contour still running'));
            }
            return of(undefined);
          }),
        );
      }),
    );
  }

  private waitUntilDownloadSlotFree(subscriptionId: string) {
    return timer(0, DOWNLOAD_CONTOUR_POLL_INTERVAL_MS).pipe(
      take(DOWNLOAD_CONTOUR_POLL_ATTEMPTS),
      exhaustMap(() => this.allAssignments(subscriptionId)),
      filter(items => inflightAssignmentIds(items, 'block').length === 0),
      take(1),
      map(() => undefined),
      defaultIfEmpty(null),
      switchMap(ready => ready === null
        ? throwError(() => new Error('download contour did not stop'))
        : of(undefined)),
    );
  }

  private allAssignments(subscriptionId: string) {
    return this.assignmentPage(subscriptionId, 1).pipe(
      expand(page => {
        const size = page.pageSize || DOWNLOAD_CONTOUR_PAGE_SIZE;
        const current = page.page ?? 1;
        const count = page.items?.length ?? 0;
        const done = current >= DOWNLOAD_CONTOUR_MAX_PAGES
          || (page.totalCount != null
            ? current * size >= page.totalCount
            : count < size);
        return done ? EMPTY : this.assignmentPage(subscriptionId, current + 1);
      }),
      map(page => page.items ?? []),
      reduce((all, items) => all.concat(items), [] as MonitoredOperationListItemResponse[]),
    );
  }

  private assignmentPage(subscriptionId: string, page: number) {
    return this.service.processSubscriptionsSubscriptionIdAssignmentsGet(
      subscriptionId,
      page,
      DOWNLOAD_CONTOUR_PAGE_SIZE,
      false,
    );
  }
}
